import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Grid,
  TextField,
  MenuItem,
  Divider,
  Stack,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  TrendingUp as TrendingUpIcon,
  Savings as SavingsIcon,
  Warning as WarningIcon,
  AutoAwesome as AIIcon,
  Category as CategoryIcon,
  Insights as InsightsIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { statementService } from '../services/statements';
import { cardService } from '../services/cards';
import { useQuery } from '@tanstack/react-query';

const StatementUpload: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: userCards = [], isLoading: cardsLoading } = useQuery({
    queryKey: ['userCards'],
    queryFn: cardService.getUserCards,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => statementService.uploadStatement(file, selectedCardId || undefined),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        message: data.message || `Successfully parsed ${data.saved_count || 0} transactions!`,
        severity: 'success',
      });
      setSelectedFile(null);
      // Invalidate all transaction-related queries to refresh Transaction History and Smart Recommendations pages
      // This will refresh the Transactions page and Spending Patterns automatically
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'transactions' || key === 'savingsAnalysis';
        }
      });
    },
    onError: (error: any) => {
      const errorMessage = error.message || error.response?.data?.message || error.response?.data?.detail || 'Failed to upload statement';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      console.error('Upload error:', error);
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      setSnackbar({
        open: true,
        message: 'Please select a PDF file',
        severity: 'error',
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      setSnackbar({
        open: true,
        message: 'Please drop a PDF file',
        severity: 'error',
      });
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  const analysis = uploadMutation.data?.analysis;
  const recommendations = analysis?.recommendations || [];
  const transactions = uploadMutation.data?.transactions || [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
          borderRadius: 4,
          p: 6,
          mb: 5,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.3)',
              }}
            >
              <DescriptionIcon sx={{ color: 'white', fontSize: 48 }} />
            </Box>
            <Box>
              <Typography variant="h3" component="h1" fontWeight={900} sx={{ color: 'white', mb: 1 }}>
                Upload Statement
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
                AI-powered PDF parsing with automatic transaction extraction and savings analysis
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }} flexWrap="wrap" gap={1}>
            <Chip
              icon={<AIIcon />}
              label="OCR & AI Extraction"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
              }}
            />
            <Chip
              icon={<CategoryIcon />}
              label="Auto-Categorization"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
              }}
            />
            <Chip
              icon={<InsightsIcon />}
              label="Savings Analysis"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
              }}
            />
          </Stack>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Upload Section */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
              },
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UploadIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Upload PDF Statement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Our AI-powered OCR extracts transactions in seconds
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Card Selection */}
              {userCards.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    select
                    label="Select Card (Optional)"
                    value={selectedCardId || ''}
                    onChange={(e) => setSelectedCardId(e.target.value ? Number(e.target.value) : null)}
                    helperText="Select which card this statement belongs to. We'll auto-detect if not selected."
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>Auto-detect from statement</em>
                    </MenuItem>
                    {userCards.map((card: any) => (
                      <MenuItem key={card.id} value={card.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CreditCardIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {card.card?.name || 'Unknown Card'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {card.card?.bank?.name || ''} • •••• {card.card_number_last4 || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              {cardsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {userCards.length === 0 && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    No cards added yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add cards to your wallet first to get better analysis and recommendations
                  </Typography>
                </Alert>
              )}

              {/* Drag & Drop Area */}
              <Box
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                  border: '3px dashed',
                  borderColor: selectedFile ? 'success.main' : 'primary.main',
                  borderRadius: 3,
                  p: 5,
                  textAlign: 'center',
                  mb: 3,
                  bgcolor: selectedFile ? 'rgba(16, 185, 129, 0.05)' : 'rgba(102, 126, 234, 0.05)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="statement-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="statement-upload" style={{ cursor: 'pointer' }}>
                  {selectedFile ? (
                    <Box>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 56, mb: 2 }} />
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                        sx={{ mt: 2 }}
                      >
                        Change File
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.7 }} />
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        Drop PDF here or click to browse
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supported format: PDF only
                      </Typography>
                    </Box>
                  )}
                </label>
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleUpload}
                disabled={!selectedFile || uploadMutation.isPending}
                startIcon={uploadMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <AIIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {uploadMutation.isPending ? 'Processing with AI...' : 'Upload & Parse Statement'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* How It Works Section */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f093fb 0%, #4facfe 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InsightsIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Typography variant="h5" fontWeight={800}>
                  How It Works
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={3}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      fontWeight: 700,
                    }}
                  >
                    1
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Upload PDF
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select your credit card statement PDF file. We support all major T&T bank formats.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      fontWeight: 700,
                    }}
                  >
                    2
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      AI Extraction
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Our OCR technology (pdfplumber + pytesseract) extracts all transactions including date, merchant, and amount in seconds.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      fontWeight: 700,
                    }}
                  >
                    3
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Auto-Categorization
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Transactions are automatically categorized (groceries, dining, travel, etc.) using merchant identifiers and AI.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      fontWeight: 700,
                    }}
                  >
                    4
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Savings Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We compare rewards earned vs. potential rewards with other cards in your wallet. Identifies "leaked rewards" where a better card was available.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      fontWeight: 700,
                    }}
                  >
                    5
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Recommendations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Get personalized suggestions showing which card to use for each transaction to maximize rewards.
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analysis Results */}
      {analysis && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom sx={{ mb: 4 }}>
            Analysis Results
          </Typography>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: 4,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                      <Typography variant="h4" fontWeight={800}>
                        TT${Number(analysis.total_spent || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Spent
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  color: 'white',
                  borderRadius: 4,
                  boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <SavingsIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                      <Typography variant="h4" fontWeight={800}>
                        TT${Number(analysis.potential_savings || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Potential Savings
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                  color: 'white',
                  borderRadius: 4,
                  boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(245, 158, 11, 0.4)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <WarningIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                      <Typography variant="h4" fontWeight={800}>
                        TT${Number(analysis.missed_savings || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Missed Savings
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Spending by Category */}
          {analysis.by_category && Object.keys(analysis.by_category).length > 0 && (
            <Card sx={{ mb: 4, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <CategoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight={800}>
                    Spending by Category
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  {Object.entries(analysis.by_category)
                    .sort(([, a], [, b]) => b - a)
                    .map(([category, amount]: [string, any]) => {
                      const percentage = analysis.total_spent > 0
                        ? (Number(amount) / Number(analysis.total_spent)) * 100
                        : 0;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={category}>
                          <Card
                            sx={{
                              borderRadius: 3,
                              border: '2px solid',
                              borderColor: 'rgba(102, 126, 234, 0.2)',
                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'rgba(102, 126, 234, 0.4)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Chip label={category} size="small" sx={{ fontWeight: 600 }} />
                                <Typography variant="h6" fontWeight={700} color="primary">
                                  TT${Number(amount).toFixed(2)}
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: '#f1f5f9',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                  },
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {percentage.toFixed(1)}% of total spending
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Missed Savings Opportunities */}
          {recommendations.length > 0 && (
            <Card sx={{ mb: 4, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <WarningIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="h5" fontWeight={800} gutterBottom>
                      Missed Savings Opportunities
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Transactions where you could have earned more rewards with a different card
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Merchant</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                        <TableCell><strong>Missed Savings</strong></TableCell>
                        <TableCell><strong>Better Card</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recommendations.slice(0, 10).map((rec: any, idx: number) => (
                        <TableRow
                          key={idx}
                          sx={{
                            '&:hover': {
                              bgcolor: 'rgba(102, 126, 234, 0.05)',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {rec.transaction || rec.merchant || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              TT${Number(rec.amount).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`TT$${Number(rec.missed_savings).toFixed(2)}`}
                              size="small"
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CreditCardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {rec.recommended_card}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {recommendations.length > 10 && (
                  <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    <Typography variant="body2">
                      Showing top 10 of {recommendations.length} missed savings opportunities
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Extracted Transactions Preview */}
          {transactions.length > 0 && (
            <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DownloadIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h5" fontWeight={800} gutterBottom>
                        Extracted Transactions
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} extracted from your statement
                      </Typography>
                    </Box>
                  </Box>
                  {uploadMutation.data?.saved_count && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`${uploadMutation.data.saved_count} Saved`}
                      sx={{
                        bgcolor: 'success.main',
                        color: 'white',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Merchant</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                        <TableCell><strong>Category</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((txn: any, idx: number) => (
                        <TableRow
                          key={idx}
                          sx={{
                            '&:hover': {
                              bgcolor: 'rgba(102, 126, 234, 0.05)',
                            },
                          }}
                        >
                          <TableCell>{txn.date || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {txn.merchant || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              TT${Number(txn.amount || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={txn.category || 'OTHER'}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {uploadMutation.data?.saved_count && (
                  <Alert severity="success" sx={{ mt: 3, borderRadius: 2 }}>
                    <Typography variant="body1" fontWeight={600} gutterBottom>
                      Successfully saved {uploadMutation.data.saved_count} transactions to your account!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      These transactions are now available in your Transaction History and will be used for future savings analysis.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StatementUpload;
